/* eslint-disable consistent-return */
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import last from 'lodash/last';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import store from '@Store';
import * as AuthUtils from '@Services/Auth/AuthUtils';
import { PreferencesSectionsMapping } from '@Pages/ProfileSettings/ProfileSettingsConstants';
import RequestUrls from '@Constants/RequestUrlsConstants';
import MessageBroker from '@Services/MessageBroker';
import { openProfileSettingsUserAgreementPrompt } from '@Pages/ProfileSettings/UserAgreementPrompt/UserAgreementPromptActions';
import Catalyst from '@Services/Catalyst';
import StructureOptionsData from '@Services/StructureOptionsData';

export class ProfileSettingsService {
  combinedInfo = null;

  user = null;

  defaultUser = null;

  loadProfileSettingsInfo = async () => {
    try {
      await this.loadUserProfile();
      await this.loadCombineInfo();
      this.buildDefaultValuesObject();
    } catch (e) {
      Logger.error(e);
    }
  };

  loadCombineInfo = async () => {
    if (this.combinedInfo) {
      return new Promise((resolve) => {
        resolve(this.combinedInfo);
      });
    }

    try {
      const data = await MessageBroker.send(RequestUrls.userCombinedInfo);
      // To get usedLib data from PREDICTION_PLAN to PREDICTION_PLAN_IKTOS
      if (get(data, 'PREDICTION_PLAN_IKTOS.preferences') && get(data, 'PREDICTION_PLAN.preferences.RX2_PREDICTION_PLAN_LIBRARIES')) {
        data.PREDICTION_PLAN_IKTOS.preferences.RX2_PREDICTION_PLAN_LIBRARIES = cloneDeep(
          get(data, 'PREDICTION_PLAN.preferences.RX2_PREDICTION_PLAN_LIBRARIES')
        );
      }
      this.combinedInfo = data;
      this.fixBooleanValues(this.combinedInfo);
      this.wrapSearchDrawingOptions();
      return this.combinedInfo;
    } catch (e) {
      Logger.reject(e);
    }
  };

  loadUserProfile = async () => {
    if (this.user) {
      return new Promise((resolve) => {
        resolve(this.user);
      });
    }

    try {
      const data = await MessageBroker.send(RequestUrls.userProfileExtended);
      // Fallback in case response does not contain Suppliers object
      if (!data.SUPPLIERS) {
        data.SUPPLIERS = {
          RX2_SUPPLIERS: {},
        };
      }
      // To get usedLib data from PREDICTION_PLAN to PREDICTION_PLAN_IKTOS
      if (get(data, 'PREDICTION_PLAN_IKTOS') && get(data, 'PREDICTION_PLAN.RX2_PREDICTION_PLAN_LIBRARIES')) {
        data.PREDICTION_PLAN_IKTOS.RX2_PREDICTION_PLAN_LIBRARIES = cloneDeep(get(data, 'PREDICTION_PLAN.RX2_PREDICTION_PLAN_LIBRARIES'));
      }

      this.user = data;
      this.fixBooleanValues(this.user);
      this.user.PROFILE.salutation = this.user.PROFILE.salutation || '';
      return this.user;
    } catch (e) {
      Logger.reject(e);
    }
  };

  getCombinedInfo = () => {
    return this.combinedInfo;
  };

  getSalutations() {
    return this.combinedInfo.PROFILE.salutations;
  }

  getSubjectAreas = () => {
    return this.combinedInfo.PROFILE.subjectAreas;
  };

  getUserAERoles = () => {
    return this.combinedInfo.PROFILE.userAERoles;
  };

  getOrganizationTypes = () => {
    return this.combinedInfo.PROFILE.organizationTypes;
  };

  get = () => {
    return this.user;
  };

  getDefault = () => {
    return this.defaultUser;
  };

  getModelValue = (model, keys) => {
    // eslint-disable-next-line no-param-reassign
    keys = !isArray(keys) ? keys.split('.') : [...keys];
    const targetKey = keys.shift();

    let result = null;

    if (!model) {
      return;
    }
    const modelProps = Object.keys(model);
    modelProps.forEach((prop) => {
      if (prop === targetKey) {
        result = keys.length ? this.getModelValue(model[prop], keys) : model[prop];
      }
    });

    if (get(model, 'preferences')) {
      const preferences = Object.keys(model.preferences);
      preferences.forEach((value) => {
        if (value === targetKey) {
          result = keys.length ? this.getModelValue(model.preferences[value], keys) : model.preferences[value];
        }
      });
    }

    return result;
  };

  parseDefaultValue = (defaults) => {
    if (!isArray(defaults)) {
      return;
    }
    let result = null;

    defaults.forEach((item) => {
      if (item.defaultValue) {
        result = item.value;
      }
    });

    return result;
  };

  parseSuppliersDefaultValue = (defaults) => {
    const keys = Object.keys(defaults);
    return keys.reduce((acc, key) => {
      const value = defaults[key];
      acc[key] = null;

      const values = get(value, 'values', []);
      values.forEach((item) => {
        if (item.defaultValue) {
          acc[key] = item.value;
        }
      });
      return acc;
    }, {});
  };

  setModelValue = (model, keys, value) => {
    if (!isArray(keys)) {
      // eslint-disable-next-line no-param-reassign
      keys = keys.split('.');
    }
    const targetKey = keys.shift();

    // eslint-disable-next-line no-unused-vars,no-restricted-syntax
    for (const prop in model) {
      if (prop === targetKey) {
        if (keys.length) {
          this.setModelValue(model[prop], keys, value);
          return;
        }
        // eslint-disable-next-line no-param-reassign
        model[prop] = value;
        return;
      }
    }

    // create new property if is not exist yet
    if (!keys.length) {
      // eslint-disable-next-line no-param-reassign
      model[targetKey] = value;
    }
  };

  migratePreferenceValues = (props, fromModel, toModel) => {
    props.forEach((prop) => {
      const defaultPropValue = this.getModelValue(fromModel, prop);
      this.setModelValue(toModel, prop, defaultPropValue);
    });
  };

  migrateSectionValues = (sections, fromModel, toModel) => {
    sections.forEach((sectionToReset) => {
      const sectionProps = PreferencesSectionsMapping[sectionToReset];
      this.migratePreferenceValues(sectionProps, fromModel, toModel);
    });
  };

  getPropertyName = (chainedProp) => {
    return last(!isArray(chainedProp) ? chainedProp.split('.') : chainedProp);
  };

  update = async (userProfile) => {
    try {
      const payload = {
        salutation: userProfile.salutation,
        institution: userProfile.institution,
        location: userProfile.location,
        jobTitle: userProfile.jobTitle,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        username: userProfile.username,
      };

      const data = await this.sendUpdateRequest(
        {
          method: 'POST',
          url: RequestUrls.userProfile.url,
        },
        payload
      );
      this.user.PROFILE = data;
      this.user.PROFILE.salutation = this.user.PROFILE.salutation || '';
      return this.user.PROFILE;
    } catch (e) {
      Logger.reject(e);
    }
  };

  changePassword = (oldPassword, newPassword) => {
    return this.sendUpdateRequest(RequestUrls.changePassword, { oldPassword, newPassword });
  };

  changeEmail = async (newEmail) => {
    try {
      await this.sendUpdateRequest(RequestUrls.changeEmail, { newEmail });
      this.user.PROFILE.email = newEmail;
    } catch (e) {
      Logger.reject(e);
    }
  };

  updatePreferences = async (preferences, sectionName) => {
    try {
      await this.sendUpdateRequest(RequestUrls.changePreferences, { preferences });

      if (sectionName === 'PREFERRED_SUPPLIERS') {
        const newValue = preferences.reduce((acc, item) => {
          acc[item.name] = item.value;
          return acc;
        }, {});
        this.setUserPreference(this.user, 'RX2_SUPPLIERS', newValue, sectionName);
      } else {
        const preferencesKeys = Object.keys(preferences);

        preferencesKeys.forEach((key) => {
          const preference = preferences[key];
          this.setUserPreference(this.user, preference.name, preference.value, sectionName);
        });
      }

      this.fireResetStructureEditorOptions(preferences);
    } catch (e) {
      return Logger.reject(e);
    }
  };

  resetToDefault = () => {
    this.updateDefaultEditor();
    this.migrateSectionValues(
      ['DEFAULT_EDITOR_SETTINGS_PREFERENCES', 'DEFAULT_EDITOR_SETTINGS_SEARCH_DRAWING', 'DEFAULT_STRUCTURE_EDITOR'],
      this.defaultUser,
      this.user
    );
  };

  resetPreferencesStructureEditor = async () => {
    try {
      await this.sendUpdateRequest(RequestUrls.resetPreferencesStructureEditor);
      this.resetToDefault();
      await StructureOptionsData.resetOptions();
    } catch (e) {
      return Logger.reject(e);
    }
  };

  resetPreferencesAutoplanner = async () => {
    try {
      await this.sendUpdateRequest(RequestUrls.resetPreferencesAutoplanner, null, { withCredentials: true });
      this.migrateSectionValues(['AUTOPLAN'], this.defaultUser, this.user);
    } catch (e) {
      return Logger.reject(e);
    }
  };

  resetPreferencesPredictionPlanner = async () => {
    try {
      await this.sendUpdateRequest(RequestUrls.resetPreferencesRetrosynthesisPlanner, null, { withCredentials: true });
      this.migrateSectionValues(['PREDICTION_PLAN'], this.defaultUser, this.user);
    } catch (e) {
      return Logger.reject(e);
    }
  };

  resetPreferencesPredictionPlannerIktos = async () => {
    try {
      await this.sendUpdateRequest(RequestUrls.resetPreferencesRetrosynthesisPlanner, null, { withCredentials: true });
      this.migrateSectionValues(['PREDICTION_PLAN_IKTOS'], this.defaultUser, this.user);
    } catch (e) {
      return Logger.reject(e);
    }
  };

  resetPreferencesConditionsAndExamples = async () => {
    try {
      await this.sendUpdateRequest(RequestUrls.resetPreferencesConditionsAndExamples, null, { withCredentials: true });
      this.migrateSectionValues(['RX2_SYNTHESIS_CONDITIONS_POSITION'], this.defaultUser, this.user);
    } catch (e) {
      return Logger.reject(e);
    }
  };

  resetPreferencesPreferredSuppliers = async () => {
    try {
      await this.sendUpdateRequest(RequestUrls.resetSupplierPreferences, null, { withCredentials: true });
      this.migrateSectionValues(['PREFERRED_SUPPLIERS'], this.defaultUser, this.user);
    } catch (e) {
      return Logger.reject(e);
    }
  };

  cleanUp = () => {
    this.user = null;
    this.combinedInfo = null;
    this.defaultUser = null;
  };

  /**
   * @ngdoc method
   * @methodOf ProfileSettings
   * @name trackProfileSettings
   * @param {String} sectionName
   * @param {Array} settings
   * @description tracks changing of profile settings
   */
  trackProfileSettings = (sectionName, settings = []) => {
    const trackingStr = this.generateTracking(sectionName, settings);
    if (trackingStr) {
      Catalyst.trackUserProfileUpdate(trackingStr);
    }
  };

  trackResetToDefault = (sectionName) => {
    let trackingStr;

    // eslint-disable-next-line default-case
    switch (sectionName) {
      case 'DEFAULT_EDITOR_SETTINGS':
        trackingStr = 'Structure/reaction editor setting:default';
        break;
      case 'AUTOPLAN':
        trackingStr = 'Autoplan:default';
        break;
      case 'PREFERRED_SUPPLIERS':
        trackingStr = 'preferred suppliers:default';
        break;
    }

    if (trackingStr) {
      Catalyst.trackUserProfileUpdate(trackingStr);
    }
  };

  wrapSearchDrawingOptions = () => {
    const searhchByPreference = this.combinedInfo.DEFAULT_EDITOR_SETTINGS.preferences.RX2_SEARCH_BY;
    const subOptionsValues = ['substructure', 'sub_hetero'];

    const subOptions = _.filter(searhchByPreference.values, (availableValue) => {
      return _.contains(subOptionsValues, availableValue.value);
    });

    searhchByPreference.values = _.filter(searhchByPreference.values, (availableValue) => {
      return !_.contains(subOptionsValues, availableValue.value);
    });

    const substructureOption = {
      subOptions,
      value: subOptions[0].value,
      label: 'As substructure',
    };
    searhchByPreference.values.splice(1, 0, substructureOption);
  };

  sendLicenseAgreementRequest = async () => {
    try {
      await MessageBroker.send(
        {
          method: 'POST',
          url: RequestUrls.userProfile.url,
        },
        {
          licenseAgreement: true,
        }
      );
    } catch (e) {
      Logger.reject(e);
    }
  };

  /**
   * @ngdoc function
   * @name _profileErrorHandler
   * @param {Object} err
   * @param {Object} requestUrl
   * @param {Object} data
   * @param {Object} options
   * @returns {Promise}
   * @description handles special profile errors, such as user agreement
   */
  profileErrorHandler = async (err, requestUrl, data, options) => {
    const errorNumber = get(err, 'errorNumber');
    if (errorNumber !== 22) {
      return Logger.reject(err);
    }

    const submit = async () => {
      try {
        await this.sendLicenseAgreementRequest();
        await this.sendUpdateRequest(requestUrl, data, options);
      } catch (e) {
        Logger.reject(e);
      }
    };

    await store.dispatch(openProfileSettingsUserAgreementPrompt({ submit }));
  };

  /**
   * @ngdoc function
   * @name _sendUpdateRequest
   * @param {Object} requestUrl
   * @param {Object} [data]
   * @param {Object} [options]
   * @returns {Promise}
   * @description wrapper for update profile requests, to be able to handle special errors
   */
  sendUpdateRequest = async (requestUrl, data, options) => {
    try {
      return await MessageBroker.send(requestUrl, data, options);
    } catch (e) {
      this.profileErrorHandler(e, requestUrl, data, options);
    }
  };

  setUserPreference = (model, preference, value, sectionName) => {
    const props = Object.keys(model);
    props.forEach((prop) => {
      if (prop === preference) {
        // eslint-disable-next-line no-param-reassign
        model[prop] = value;
        return;
      }
      if (isObject(model[prop])) {
        this.setUserPreference(model[prop], preference, value, sectionName);
      }
    });
  };

  updateDefaultEditor = () => {
    const currentUser = AuthUtils.getUser();
    currentUser.details.settings.structureEditorPreselected = get(
      this.defaultUser,
      'DEFAULT_EDITOR_SETTINGS.RX2_STRUCTURE_EDITOR_PRESELECTED'
    );
    AuthUtils.updateUser(currentUser);
  };

  fixBooleanValues = (modelToFix) => {
    const keys = Object.keys(modelToFix);
    keys.forEach((key) => {
      // eslint-disable-next-line no-prototype-builtins
      if (modelToFix.hasOwnProperty(key)) {
        if (isObject(modelToFix[key])) {
          this.fixBooleanValues(modelToFix[key]);
        } else if (modelToFix[key] === 'true' || modelToFix[key] === 'false') {
          // eslint-disable-next-line no-param-reassign
          modelToFix[key] = modelToFix[key] === 'true';
        }
      }
    });
  };

  buildDefaultValuesObject = () => {
    this.defaultUser = cloneDeep(this.user);
    const allSections = Object.keys(PreferencesSectionsMapping);
    allSections.forEach((section) => {
      return PreferencesSectionsMapping[section].forEach((sectionProperty) => {
        const defaults = this.getModelValue(this.combinedInfo, sectionProperty);
        if (sectionProperty === 'SUPPLIERS.RX2_SUPPLIERS') {
          const defaultValue = this.parseSuppliersDefaultValue(defaults.preferences || defaults.values || defaults);
          this.setModelValue(this.defaultUser, sectionProperty, defaultValue);
          return;
        }

        if (defaults === null) {
          this.setModelValue(this.defaultUser, sectionProperty, null);
        } else if (sectionProperty !== 'PREDICTION_PLAN_IKTOS.RX2_PREDICTION_PLAN_LIBRARIES') {
          const defaultValue = this.parseDefaultValue(defaults.preferences || defaults.values || defaults);
          this.setModelValue(this.defaultUser, sectionProperty, defaultValue);
        }
      });
    });

    // RXFE-3032
    // FE: Enable retrieving the default structure/reaction drawing search
    // settings (preferences) retrieval from A&E
    const preferencesKeys = [
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_ISOTOPES',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_STEREO',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_TAUTOMERS',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_KEEP_FRAGMENTS',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_MIXTURES',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_MARKUSH',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_RADICALS',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_EXTRA_RINGS',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_CHARGES',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_SALTS',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_IGNORE_ATOM_MAPPING',
    ];

    preferencesKeys.forEach((preference) => {
      // TODO: fix this workaround after BE implements proper model for keep fragments option
      // RXFE-5124: setting proper default value for keep fragments option
      // (by design we receive 'keep_separate' as default value which is incorrect – it should be empty)
      if (preference === 'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_KEEP_FRAGMENTS') {
        this.setModelValue(this.defaultUser, preference, '');
        return;
      }
      const defaults = this.getModelValue(this.combinedInfo, preference).values;
      const modelDefault = defaults.find((item) => item.defaultValue);
      if (modelDefault) {
        this.setModelValue(this.defaultUser, preference, modelDefault.value);
      }
    });
  };

  fireResetStructureEditorOptions = (payload) => {
    const editorRelatedSections = ['DEFAULT_EDITOR_SETTINGS_SEARCH_DRAWING', 'DEFAULT_EDITOR_SETTINGS_PREFERENCES'];

    const editorRelatedProps = editorRelatedSections.reduce((accumulator, section) => {
      return [...accumulator, ...PreferencesSectionsMapping[section]];
    }, []);

    const isRelatedPropertyChanged = payload.some((payloadItem) => {
      return editorRelatedProps.find((prop) => this.getPropertyName(prop) === payloadItem.name);
    });

    if (isRelatedPropertyChanged) {
      StructureOptionsData.resetOptions();
    }
  };

  /**
   * @ngdoc function
   * @name _generateTracking
   * @param {String} sectionName
   * @param {Array} settings
   * @returns {String}
   * @description resolver based on section name
   */
  generateTracking = (sectionName, settings) => {
    let tracking;

    const generateTrackingForPreferences = () => {
      const incl = settings.filter((setting) => setting.isInclude && setting.value);
      const other = settings.filter((setting) => !setting.isInclude && setting.value !== '');
      const inclValue = incl.map((include) => include.readableName).join('|');
      const inclStr = !isEmpty(incl) ? `Include=${inclValue},` : '';
      const otherStr = other.map((setting) => `${setting.readableName}=${setting.readableValue}`).join(',');
      return `Preferences:${inclStr}${otherStr}`;
    };

    const generateTrackingForSearchStructureAs = () => {
      const value = get(settings, '0.value');
      let str = get(settings, '0.readableValue');

      // eslint-disable-next-line default-case
      switch (value) {
        case 'sub_hetero':
          str = 'as substructure:on heteroatoms';
          break;
        case 'substructure':
          str = 'as substructure:on all atoms';
          break;
      }
      return `Search structure:${str}`;
    };

    const generateTrackingForAutoplan = () => {
      const settingsStr = settings.map((setting) => `${setting.readableName}=${setting.readableValue}`).join(',');
      return `Autoplan:${settingsStr}`;
    };

    const generateTrackingForPrefferedSuppliers = () => {
      const mappedSettings = settings.filter((setting) => !!setting.value).map((setting) => setting.readableName);
      return `${mappedSettings.length}|preferred suppliers: ${mappedSettings.join('^')}`;
    };

    // eslint-disable-next-line default-case
    switch (sectionName) {
      case 'DEFAULT_STRUCTURE_EDITOR':
        tracking = `Structure editor:${get(settings, '0.readableValue')}`;
        break;
      case 'DEFAULT_EDITOR_SETTINGS_SEARCH_DRAWING':
        tracking = generateTrackingForSearchStructureAs();
        break;
      case 'DEFAULT_EDITOR_SETTINGS_PREFERENCES':
        tracking = generateTrackingForPreferences();
        break;
      case 'AUTOPLAN':
        tracking = generateTrackingForAutoplan();
        break;
      case 'PREFERRED_SUPPLIERS':
        tracking = generateTrackingForPrefferedSuppliers();
        break;
      case 'RESULT_PER_PAGE':
        tracking = `Results:${get(settings, '0.readableValue')}`;
        break;
      case 'RX2_SYNTHESIS_CONDITIONS_POSITION':
        tracking = `Synthesis planner:${get(settings, '0.readableValue')}`;
        break;
    }

    return tracking;
  };
}

const ProfileSettings = new ProfileSettingsService();
export default ProfileSettings;
